import { type SECRPriorYearOverrides } from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import { cn, formatNumber } from '@shared/lib/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ALL_EMISSION_KEYS, INTENSITY_METRIC_OPTIONS } from '../constants';
import {
  type EmissionRowKey,
  type PrevSource,
  type SecrFormValues,
} from '../schema';

interface IntensityRatioTableProps {
  source: PrevSource;
  currentTotalTco2e: number | null;
  priorYearComputed?: SECRPriorYearOverrides | null;
}

export function IntensityRatioTable({
  source,
  currentTotalTco2e,
  priorYearComputed,
}: IntensityRatioTableProps) {
  const { watch, setValue } = useFormContext<SecrFormValues>();
  const intensityMetric = watch('intensityMetric');
  const prevIntensityValue = watch('prevIntensityValue');
  const currentIntensityValue = watch('currentIntensityValue');
  const formValues = watch();

  // When the prior year was logged on My Emissions and had a metric type,
  // lock this year's metric to match — the locked prior intensity value is
  // measured in that unit, so the user can't change it without breaking the
  // ratio.
  const lockedPriorMetric =
    source === 'my_emissions'
      ? (priorYearComputed?.intensityMetricType ?? null)
      : null;
  const isMetricLocked = lockedPriorMetric != null;

  React.useEffect(() => {
    if (isMetricLocked && intensityMetric !== lockedPriorMetric) {
      setValue('intensityMetric', lockedPriorMetric, { shouldDirty: true });
    }
  }, [isMetricLocked, lockedPriorMetric, intensityMetric, setValue]);

  const option = INTENSITY_METRIC_OPTIONS.find(
    (o) => o.value === intensityMetric
  );
  const unitLabel = option?.unit ?? '-';

  const prevTotalTco2e =
    source === 'none'
      ? null
      : source === 'my_emissions'
        ? (priorYearComputed?.totalTonnes ?? null)
        : ALL_EMISSION_KEYS.reduce(
            (sum, key: EmissionRowKey) =>
              sum + (Number(formValues[key]?.prevYear) || 0),
            0
          );

  const effectivePrevIntensityValue =
    source === 'my_emissions'
      ? (priorYearComputed?.intensityValue ?? null)
      : prevIntensityValue;

  const fmt = (n: number | null | undefined) => {
    if (n == null || isNaN(n)) return '-';
    if (n > 0 && n < 0.01)
      return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
    return formatNumber(n);
  };

  const prevEmissionsRatio =
    prevTotalTco2e != null && effectivePrevIntensityValue
      ? prevTotalTco2e / effectivePrevIntensityValue
      : null;
  const currentEmissionsRatio =
    currentTotalTco2e != null && currentIntensityValue
      ? currentTotalTco2e / currentIntensityValue
      : null;

  const adornment = unitLabel !== '-' ? unitLabel : undefined;

  const isOneYear = source === 'none';

  return (
    <div className={cn('space-y-3', isOneYear && 'w-[71%]')}>
      <div
        className={cn(
          'grid items-end gap-0',
          isOneYear ? 'grid-cols-[59.15%_40.85%]' : 'grid-cols-[42%_29%_29%]'
        )}
      >
        <SelectFormField
          name="intensityMetric"
          label="Intensity metric"
          placeholder="Select a metric..."
          options={INTENSITY_METRIC_OPTIONS}
          showIcon
          showDescription
          includeErrorMessage
          disabled={isMetricLocked}
          className="[&_button[role=combobox]]:rounded-r-none"
        />

        {source === 'my_emissions' ? (
          <div className="bg-muted/40 -ml-px flex h-10 items-center justify-end border border-input px-3 text-sm">
            {priorYearComputed?.intensityValue != null ? (
              <>
                <span>{formatNumber(priorYearComputed.intensityValue)}</span>
                {adornment && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    {adornment}
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        ) : source === 'off_platform' ? (
          <InputFormField
            name="prevIntensityValue"
            type="number"
            placeholder="-"
            adornmentRight={adornment}
            inputClassName="-ml-px rounded-none text-right"
          />
        ) : null}

        <InputFormField
          name="currentIntensityValue"
          type="number"
          placeholder="-"
          adornmentRight={adornment}
          inputClassName="-ml-px rounded-l-none text-right"
        />
      </div>

      <div className="overflow-hidden rounded-lg border bg-gray-00 shadow-sm">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            <col className={isOneYear ? 'w-[59.15%]' : 'w-[42%]'} />
            {!isOneYear && <col className="w-[29%]" />}
            <col className={isOneYear ? 'w-[40.85%]' : 'w-[29%]'} />
          </colgroup>
          <tbody>
            <tr>
              <td className="border-r border-primary-100 px-3 py-2.5 text-right text-sm font-medium">
                Intensity ratio
              </td>
              {!isOneYear && (
                <td className="border-l border-primary-100 px-3 py-2.5 text-right text-sm">
                  {prevEmissionsRatio != null ? (
                    <>
                      <span className="font-medium">
                        {fmt(prevEmissionsRatio)}
                      </span>
                      <span className="ml-1 text-xs text-muted-foreground">
                        tCO₂e/{unitLabel}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              )}
              <td className="border-l border-teal-100 px-3 py-2.5 text-right text-sm">
                {currentEmissionsRatio != null ? (
                  <>
                    <span className="font-medium">
                      {fmt(currentEmissionsRatio)}
                    </span>
                    <span className="ml-1 text-xs text-muted-foreground">
                      tCO₂e/{unitLabel}
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
